/**
 * Where a treatment case sits in the staff's own pipeline.
 *
 * These are stages of *our* work, not states from the practice management
 * system. The PMS says whether treatment is scheduled; it has no idea whether
 * anyone has phoned about it. That gap is the whole point of the board: the
 * columns track the conversation, the card carries the clinical facts.
 *
 * Four working stages, rendered as the board's columns. `system:treatment`
 * (see `systemPipelines.ts`) carries two more, `Scheduled` and
 * `Lost`, both `exit: true`: real stages a card can sit at, just
 * not ones this board draws a column for. `BoardStage` only names the four a
 * card actively works through; the exits are addressed by name directly
 * (`EXIT_SCHEDULED`/`EXIT_DECLINED` below) since they are not a choice the
 * segmented control in `TreatmentBoardComponent.tsx` offers.
 */
export type BoardStage = `to-reach` | `reached` | `talking` | `ready`

export const BOARD_STAGES: { id: BoardStage; label: string; blurb: string }[] = [
  { id: `to-reach`, label: `Not contacted`, blurb: `Nobody has reached out about this treatment yet` },
  { id: `reached`, label: `Reached out`, blurb: `Called or texted, waiting to hear back` },
  { id: `talking`, label: `In conversation`, blurb: `They answered and you are working through it` },
  { id: `ready`, label: `Ready to schedule`, blurb: `They said yes, this needs a time on the schedule` },
]

/**
 * A dot per working stage, for the table's stage cell.
 *
 * The board says where a case sits by which column it is in, so it needs no
 * colour; a table row has to say it in the width of one cell. The run goes
 * cold to warm as the conversation warms up, and lands on amber at `ready`
 * because that stage is the one asking somebody to do something.
 */
const STAGE_DOTS: Record<BoardStage, string> = {
  'to-reach': `bg-(--line-strong)`,
  reached: `bg-blue-400`,
  talking: `bg-indigo-500`,
  ready: `bg-amber-400`,
}

/**
 * The system pipeline the old board's columns are stages of. A lane carries
 * its own id (`to-reach` etc. on the old board, a stage id on the list); a
 * write speaks in the stage's full name (`Not contacted` etc.). `move` is the
 * only place that translation happens; the two exits below are already
 * spelled the way the pipeline wants them.
 */
export const TREATMENT_PIPELINE_ID = `system:treatment`

/** The two stage names `system:treatment` marks `exit: true`. */
export const EXIT_SCHEDULED = `Scheduled`
export const EXIT_DECLINED = `Lost`

/**
 * Every stage a case can sit at, exits included, in the order it travels
 * through them.
 *
 * The board never needs this list: its four columns come from `BOARD_STAGES`
 * and its two exits are drop zones drawn separately. The table does, because
 * one dropdown has to offer every destination a drag could reach, and a table
 * row has nowhere to be dragged to.
 */
export const TREATMENT_STAGES: { name: string; blurb: string; dot: string }[] = [
  ...BOARD_STAGES.map((stage) => ({ name: stage.label, blurb: stage.blurb, dot: STAGE_DOTS[stage.id] })),
  { name: EXIT_SCHEDULED, blurb: `Booked, so it leaves the board`, dot: `bg-green-500` },
  { name: EXIT_DECLINED, blurb: `They said no`, dot: `bg-red-500` },
]
